import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Handshake, Globe, Mountain, Sun, Snowflake } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

const hubs = [
  {
    name: "Utah",
    city: "Salt Lake City, UT",
    country: "USA",
    icon: Mountain,
    coverage: "West Coast & Mountain Region",
    partners: "20+ partners",
    link: "/locations/utah",
  },
  {
    name: "North Carolina",
    city: "Greensboro, NC",
    country: "USA",
    icon: Sun,
    coverage: "East Coast & Southeast",
    partners: "30+ partners",
    link: "/locations/north-carolina",
  },
  {
    name: "Ontario",
    city: "Mississauga, ON",
    country: "Canada",
    icon: Snowflake,
    coverage: "Canada & Great Lakes",
    partners: "25+ partners",
    link: "/locations/ontario",
  },
];

export function LocationsTeaser() {
  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
              North American Coverage
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Connected to Warehouses Across North America
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              We're connected with partner warehouses and suppliers throughout the US and Canada. Our low-overhead brokerage model lets us shop around and find you the best deals on quality used racking.
            </p>

            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <Handshake className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium">75+ Partner Locations</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium">3 Regional Offices</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium">USA + Canada</span>
              </div>
            </div>

            <Button asChild variant="accent">
              <Link to="/locations">
                View All Locations
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </motion.div>

          {/* Hubs grid */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {hubs.map((hub) => (
              <Link
                key={hub.name}
                to={hub.link}
                className="group flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-accent/50 hover:shadow-elevated transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <hub.icon className="h-6 w-6 text-accent" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-foreground">{hub.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {hub.country === "Canada" ? "🇨🇦" : "🇺🇸"} {hub.country}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{hub.coverage}</p>
                  <p className="text-xs text-accent font-medium">{hub.partners}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
